import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { s, authHeaders, Toast, getODataRows } from './listPageStyles';
import { RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { useDistributorUser } from '../hooks/useDistributorUser';
import { fetchProductUnits } from './invoiceShared';
import { useStockBalances } from './stockBalance';
import { toDateTimeOffset } from '../utils/format';

// Belge türü konfigürasyonu (variant → davranış).
const VARIANTS = {
  entry: {
    documentType: 1,
    title: 'Stok Girişi',
    subtitle: 'Depoya ürün girişi. Kaydedildiğinde stok bakiyesi artar.',
    transfer: false,
    saveLabel: 'Girişi Kaydet',
  },
  exit: {
    documentType: 2,
    title: 'Stok Çıkışı',
    subtitle: 'Depodan ürün çıkışı. Kaydedildiğinde stok bakiyesi azalır.',
    transfer: false,
    saveLabel: 'Çıkışı Kaydet',
  },
  transfer: {
    documentType: 3,
    title: 'Stok Transferi',
    subtitle: 'Bir depodan diğerine ürün aktarımı. Çıkış ve giriş deposu aynı olamaz.',
    transfer: true,
    saveLabel: 'Transferi Kaydet',
  },
};

const STOCK_DOCS_API = '/api/stockdocuments';

function todayInputValue() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}

function newKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `k_${Math.floor(Math.random() * 1e9)}_${Date.now ? '' : ''}${performance.now()}`;
}

function productLabel(p) {
  const code = p.textId || p.TextId || '';
  const name = p.name || p.Name || '';
  return name ? `${code} - ${name}` : code;
}

// #722/#738 Bakiye miktarını tr-TR formatında göster (dosyadaki toplam gösterimiyle tutarlı).
function formatBalance(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 4 });
}

// #767 Düzenleme modu: stok belgesinin BACKEND'de ayrı bir Update endpoint'i YOK (yalnız Create=insert ve
// Cancel=soft-delete var). Belge ledger'a post edilmiş olduğundan yerinde güncelleme yapılamaz; "düzenle"
// eski belgeyi İPTAL edip (Cancel → net stok etkisi geri sarılır) yenisini Create eder. Böylece defter
// tutarlı kalır ve yalnız mevcut endpoint'ler kullanılır. editId verilirse bileşen bu modda açılır.
// #767.3 `readOnly`: İZLEME modu. Belge, kayıt girişiyle AYNI ekranda açılır ama tüm alanlar kilitli
// ve kaydetme/temizleme yok. Talep buydu: "İzleme butonu ile açtığımızda çok basit bir ekran geliyor;
// Fatura/İrsaliye/Sipariş belgelerinde olduğu gibi kayıt giriş ekranı gibi açılıp izlenebilmeli."
// Prefill yolu düzenleme ile ORTAK (editId) — böylece iki ayrı yükleme mantığı doğup birbirinden sapmaz.
export function StockDocumentPage({ variant = 'entry', editId = null, readOnly = false, onSaved, onBack }) {
  const cfg = VARIANTS[variant] || VARIANTS.entry;
  const isEdit = !!editId && !readOnly;
  const isView = !!editId && readOnly;
  // Prefill her iki modda da koşar (düzenleme + izleme) — tek yükleme yolu.
  const shouldPrefill = !!editId;
  // Menü aksiyon hakkı: YENİ belge → Add, düzenleme → Edit (yoksa Add) hakkı ister.
  // usePageActions aktif route'tan (/stock-entry · /stock-exit · /stock-transfer) menüyü kendisi çözer.
  const { canAdd, canEdit } = usePageActions();
  // #767.3 İzleme modunda kaydetme YOK.
  const canSubmit = isView ? false : (isEdit ? (canEdit || canAdd) : canAdd);
  usePageMeta(cfg.title, cfg.subtitle);
  // #738.3 Distribütör tipi kullanıcıda yetkili distribütör otomatik seçili + değiştirilemez.
  const { isDistributor, vendorId: lockedVendorId } = useDistributorUser();

  const [warehouses, setWarehouses] = useState([]);
  // #767.1 Depo listesi çekimi tamamlandı mı (boş/başarısız dâhil) — prefill kapısı bunu bekler.
  const [warehousesLoaded, setWarehousesLoaded] = useState(false);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // Başlık alanı
  const [docDate, setDocDate] = useState(todayInputValue());
  // #60 Distribütör: depo seçimini DARALTAN kriter. Belgeye ayrıca yazılmaz — StockDocument.VendorId
  // backend'de zaten depodan türetilir (StockDocumentsController.cs:113-119 → ResolveVendorId).
  const [vendorId, setVendorId] = useState('');
  const [vendors, setVendors] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');          // entry/exit
  const [sourceWarehouseId, setSourceWarehouseId] = useState(''); // transfer çıkış
  const [targetWarehouseId, setTargetWarehouseId] = useState(''); // transfer giriş
  const [documentNo, setDocumentNo] = useState('');
  const [description, setDescription] = useState('');

  // Kalemler
  const [lines, setLines] = useState([]);
  // #797.1 Düzenlemede belgenin AÇILIŞTAKİ kalemleri — stok kolonundaki canlı düşümün referansı.
  const [pickerValue, setPickerValue] = useState('');
  // #767 Düzenleme modunda mevcut belge yüklenirken form kilitli.
  const [editLoading, setEditLoading] = useState(shouldPrefill);

  // #722/#738/#765.3 Bakiye mantığı ARTIK PAYLAŞILAN hook'ta (components/stockBalance.js).
  // Burada bir kopyası vardı ve kopya, düzeltilmesi gereken hatayı da taşıyordu (effect kendi
  // yazdığı state'i dinleyip uçuşta olan isteği iptal ediyordu → hücre sonsuza dek "…").
  // Tek kaynağa bağlanınca aynı hata iki yerde ayrı ayrı düzeltilmek zorunda kalmıyor.

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const warehouseMap = useMemo(() => new Map(warehouses.map((w) => [w.id, w])), [warehouses]);
  const warehouseLabel = useCallback((id) => {
    const w = warehouseMap.get(id);
    if (!w) return '';
    return w.name ? `${w.textId || ''} - ${w.name}`.replace(/^ - /, '') : (w.textId || '');
  }, [warehouseMap]);

  const vendorMap = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);
  const vendorLabel = useCallback((id) => {
    const v = vendorMap.get(id);
    if (!v) return '';
    return v.name ? `${v.textId || ''} - ${v.name}`.replace(/^ - /, '') : (v.textId || '');
  }, [vendorMap]);

  useEffect(() => {
    fetch('/api/warehouses/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((w) => setWarehouses(unwrapLookupItems(w)))
      .catch(() => {})
      // #767.1 Çekim BİTTİ bilgisi listenin DOLU olmasından ayrıdır: uç boş dizi dönebilir
      // (kullanıcının veri kapsamında depo yok) ya da hata verebilir. Prefill "depo listesi
      // gelsin" diye beklerken bu iki durumda sonsuza dek bekliyordu → ekran "Yükleniyor…".
      .finally(() => setWarehousesLoaded(true));
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((v) => setVendors(unwrapLookupItems(v)))
      .catch(() => {});
  }, []);

  // #738.3 Distribütör tipi kullanıcıda yetkili distribütör otomatik seçili kalır (reset sonrası da).
  useEffect(() => {
    if (isDistributor && lockedVendorId && String(vendorId) !== String(lockedVendorId)) setVendorId(lockedVendorId);
  }, [isDistributor, lockedVendorId, vendorId]);

  // #60 Depo listesi seçili distribütöre bağlıdır (Warehouse.VendorId). Distribütör seçilmeden depo
  // seçilemez — alanlar kilitli. Kapsam sunucuda zaten uygulanıyor (/Select → AllowedWarehouseIds),
  // burada yalnız distribütöre göre daraltıyoruz. Aynı desen: WarehouseStockResetDetailPage.js:46-49.
  const scopedWarehouses = useMemo(
    () => (vendorId ? warehouses.filter((w) => String(w.vendorId ?? w.VendorId) === String(vendorId)) : []),
    [warehouses, vendorId],
  );

  // Distribütör değişince, artık kapsam dışında kalan depo seçimleri temizlenir — aksi halde
  // "HB Gıda distribütörü + başka distribütörün deposu" gibi çelişkili bir belge kaydedilebilirdi.
  useEffect(() => {
    const ok = (id) => !id || scopedWarehouses.some((w) => String(w.id) === String(id));
    if (!ok(warehouseId)) setWarehouseId('');
    if (!ok(sourceWarehouseId)) setSourceWarehouseId('');
    if (!ok(targetWarehouseId)) setTargetWarehouseId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, scopedWarehouses]);

  // #722/#738 Bakiyenin okunacağı depo: giriş/çıkışta tek depo, transferde ÇIKIŞ deposu
  // (kullanıcı çıkış deposundaki mevcut stoğu görmek ister).
  const stockWarehouseId = cfg.transfer ? sourceWarehouseId : warehouseId;
  // #765.3 Kalemlerin bakiyesi paylaşılan hook'tan gelir (tek kaynak).
  const { balanceOf, ready: balanceReady, reload: reloadStock } = useStockBalances(
    vendorId, stockWarehouseId, lines.map((l) => l.productId),
  );

  // --- Ürün ekleme ---
  const fetchProduct = useCallback(async (filter) => {
    const url = `/api/products?$top=1&light=1&$filter=${encodeURIComponent(filter)}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return null;
    const rows = getODataRows(await res.json());
    return rows[0] || null;
  }, []);

  // #766 Kalem eklemeden ÖNCE zorunlu başlık alanları dolu olmalı. Giriş/çıkışta distribütör+depo;
  // transferde distribütör+çıkış+giriş deposu. Aksi halde ekleme engellenir ve uyarı gösterilir.
  const headerReady = cfg.transfer
    ? !!(vendorId && sourceWarehouseId && targetWarehouseId)
    : !!(vendorId && warehouseId);
  const headerHint = cfg.transfer
    ? 'Önce distribütör ile çıkış ve giriş deposunu seçin'
    : 'Önce distribütör ve depo seçin';

  // #739 Birimler ProductUnit tablosundan (ForProduct) beslenir — Fatura/İrsaliye/Sipariş ile aynı
  // kaynak. Ürünün tanımlı birimi yoksa Unit1 (yoksa "Adet") tek baz birim olarak kullanılır.
  // #765.2 unitPrice = birim fiyat (KDV hariç, bilgi amaçlı). preset (düzenleme yüklemesi) verilirse
  // miktar/birim/fiyat mevcut kalemden gelir.
  const addProductLine = useCallback(async (product, preset = null) => {
    if (!product) return;
    const base = product.unit1 || product.Unit1 || 'Adet';
    const fetched = await fetchProductUnits(product.id, authHeaders(), base);
    const units = fetched.length ? fetched : [{ name: base, multiplier: 1 }];
    const presetUnit = preset ? units.find((u) => u.name === preset.unitName) : null;
    // #755 VARSAYILAN BİRİM: ürün kartındaki "Varsayılan Ürün Birimi" (Product.Unit = 1..5) stok
    // belgesi kaleminde de ÖN-SEÇİLİR (Sipariş/Fatura/İrsaliye ile aynı desen). 1/boş = taban birim;
    // kartta tanımlı birim ürünün birim listesinde yoksa yine tabana düşülür.
    const duo = Number(product.unit ?? product.Unit);
    const defaultUnitName = duo >= 1 && duo <= 5
      ? String(product[`unit${duo}`] ?? product[`Unit${duo}`] ?? '').trim()
      : '';
    const defaultUnit = defaultUnitName ? units.find((u) => u.name === defaultUnitName) : null;
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        productId: product.id,
        label: productLabel(product),
        units,
        unitName: (presetUnit ?? defaultUnit ?? units[0]).name,
        multiplier: (presetUnit ?? defaultUnit ?? units[0]).multiplier,
        quantity: preset ? String(preset.quantity ?? '1') : '1',
        unitPrice: preset && preset.unitPrice != null ? String(preset.unitPrice) : '',
      },
    ]);
  }, []);

  const addById = useCallback(async (id) => {
    if (!id) return;
    // #766 Başlık hazır değilse kalem ekleme (picker'ı da temizle).
    if (!headerReady) { showToast('error', headerHint); setPickerValue(''); return; }
    const product = await fetchProduct(`Id eq ${id}`);
    if (!product) { showToast('error', 'Ürün bilgisi alınamadı'); return; }
    addProductLine(product);
    setPickerValue('');
  }, [fetchProduct, addProductLine, headerReady, headerHint]);

  // #767 Düzenleme: mevcut belgeyi yükle ve formu doldur. Depo alanları için önce depo listesi
  // gelmiş olmalı — aksi halde kapsam-temizleme effect'i (scopedWarehouses) prefill'i sıfırlar.
  // TEK KEZ çalışır (depo listesi çekimi tamamlanınca tetiklenir).
  //
  // #779 Tek-kez bayrağı STATE OLAMAZ. Eskiden `editInitialized` bir useState'ti ve effect'in hem
  // gövdesinde set ediliyor hem dep listesinde duruyordu: setEditInitialized(true) → yeni render →
  // deps değişti → React bu effect'in CLEANUP'ını koşturdu → cancelled=true. `await fetch` dönerken
  // prefill'in tamamı `if (cancelled) return` ile çöpe gidiyordu (depo boş, kalem boş) ve finally'deki
  // `if (!cancelled) setEditLoading(false)` de atlandığı için buton sonsuza dek "Yükleniyor…" kalıp
  // düzenlemeyi imkânsız kılıyordu. Ref render tetiklemediği için zincir kırılır.
  const editInitializedRef = useRef(false);
  useEffect(() => {
    // #767.1 Kapı artık `warehouses.length === 0` DEĞİL, "çekim tamamlandı" koşulu. Eskiden depo
    // listesi boş dönerse (kapsamda depo yok / uç hata verdi) effect her render'da erkenden çıkıyor,
    // `editLoading` hiç false olmuyordu: Düzenle ile açılan belge sonsuza dek "Yükleniyor…" kalıyor
    // ve kaydetme düğmesi kilitli duruyordu. Depo listesi boşken de belgeyi doldurmak DOĞRUdur —
    // alanlar seçili değeri gösteremese bile kalemler ve başlık okunur.
    if (!shouldPrefill || editInitializedRef.current || !warehousesLoaded) return;
    editInitializedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${STOCK_DOCS_API}/GetById?Id=${editId}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Belge alınamadı');
        const body = await res.json();
        const doc = body?.data ?? body;
        if (cancelled || !doc) return;
        if (doc.vendorId) setVendorId(doc.vendorId);
        if (doc.documentDate) setDocDate(String(doc.documentDate).slice(0, 10));
        setDocumentNo(doc.documentNo || '');
        setDescription(doc.description || '');
        if (cfg.transfer) {
          setSourceWarehouseId(doc.sourceWarehouseId || '');
          setTargetWarehouseId(doc.targetWarehouseId || '');
        } else {
          setWarehouseId(doc.warehouseId || '');
        }
        // Kalemler: her biri için ürün kartı + birim listesi çekilir, miktar/birim/fiyat prefill edilir.
        for (const l of (doc.lines || [])) {
          if (cancelled) return;
          // eslint-disable-next-line no-await-in-loop
          const product = await fetchProduct(`Id eq ${l.productId}`);
          if (cancelled) return;
          // eslint-disable-next-line no-await-in-loop
          if (product) await addProductLine(product, { quantity: l.quantity, unitName: l.unitName, unitPrice: l.unitPrice });
        }
      } catch (e) {
        if (!cancelled) showToast('error', e.message || 'Belge yüklenemedi');
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();
    // Bayrağı da geri al: bu cleanup yalnız unmount ya da editId/depo-listesi değişiminde koşar;
    // ikisinde de yarıda kalan yükleme yeniden denenebilir olmalı.
    return () => { cancelled = true; editInitializedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPrefill, warehousesLoaded, editId]);

  const updateLine = (key, patch) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const onUnitChange = (key, unitName) => {
    const line = lines.find((l) => l.key === key);
    const u = line?.units.find((x) => x.name === unitName);
    updateLine(key, { unitName, multiplier: u ? u.multiplier : 1 });
  };

  // --- Kaydet ---
  const resetForm = () => {
    setLines([]);
    setDocumentNo('');
    setDescription('');
    setPickerValue('');
    setVendorId('');
    setWarehouseId('');
    setSourceWarehouseId('');
    setTargetWarehouseId('');
    setDocDate(todayInputValue());
  };

  const validate = () => {
    // #60 Depo distribütöre bağlı seçildiği için distribütör önce gelir.
    if (!vendorId) return 'Distribütör seçiniz';
    if (cfg.transfer) {
      if (!sourceWarehouseId) return 'Çıkış deposu seçiniz';
      if (!targetWarehouseId) return 'Giriş deposu seçiniz';
      if (sourceWarehouseId === targetWarehouseId) return 'Çıkış ve giriş deposu aynı olamaz';
    } else if (!warehouseId) {
      return 'Depo seçiniz';
    }
    if (lines.length === 0) return 'En az bir ürün kalemi ekleyin';
    for (const l of lines) {
      const q = Number(l.quantity);
      if (Number.isNaN(q) || q <= 0) return `Geçerli bir miktar giriniz: ${l.label}`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { showToast('error', err); return; }
    const payload = {
      documentType: cfg.documentType,
      documentNo: documentNo || null,
      documentDate: toDateTimeOffset(docDate),
      warehouseId: cfg.transfer ? null : warehouseId,
      sourceWarehouseId: cfg.transfer ? sourceWarehouseId : null,
      targetWarehouseId: cfg.transfer ? targetWarehouseId : null,
      description: description || null,
      lines: lines.map((l, i) => ({
        productId: l.productId,
        quantity: Number(l.quantity),
        unitName: l.unitName,
        multiplier: Number(l.multiplier) > 0 ? Number(l.multiplier) : 1,
        lineOrder: i + 1,
      })),
    };
    setSaving(true);
    try {
      // #767/#798 DÜZENLEME = İPTAL + YENİ, ama TEK ÇAĞRIDA. Ledger'a post edilmiş belgenin yerinde
      // güncellemesi yok; eski belge iptal edilip (net stok etkisi geri sarılır) yenisi oluşturulur.
      //
      // #798 Bu iki adım ESKİDEN İSTEMCİDEN ayrı ayrı çağrılıyordu ve arası atomik değildi: Cancel
      // geçip Create negatif-stok gate'ine takılınca kullanıcının belgesi SİLİNMİŞ, yenisi
      // yazılmamış oluyordu (ekran listeye dönüyor, belge listede yok). Artık backend'deki `Update`
      // ucu ikisini tek transaction'da yapar — Create reddedilirse iptal de yazılmaz, belge kalır.
      const res = await fetch(`${STOCK_DOCS_API}/${isEdit ? 'Update' : 'Create'}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(isEdit ? { ...payload, id: editId } : payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || (isEdit ? 'Belge güncellenemedi' : 'Belge kaydedilemedi'));
      }
      showToast('success', `${cfg.title} başarıyla ${isEdit ? 'güncellendi' : 'kaydedildi'}`);
      // #799 Belge deftere işlendi → stok kolonu artık YENİ bakiyeyi göstermeli.
      reloadStock();
      resetForm();
      if (onSaved) onSaved();
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = {
    background: 'var(--card-bg, #fff)',
    border: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))',
    borderRadius: 12,
    padding: 18,
    marginBottom: 16,
  };
  const sectionTitle = { margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' };

  const totalBase = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.multiplier) || 1), 0);

  return (
    <div style={s.container}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {onBack && (
          <button type="button" style={{ ...s.btnSecondary, marginTop: 2 }} onClick={onBack}>← Listeye Dön</button>
        )}
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{isView ? `${cfg.title} — Görüntüle` : isEdit ? `${cfg.title} Düzenle` : (onBack ? `Yeni ${cfg.title}` : cfg.title)}</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>{isView ? 'Kayıt salt okunur görüntülenir. Değişiklik için listeden Düzenle\'yi kullanın.' : isEdit ? 'Belge iptal edilip yeni haliyle yeniden kaydedilir (stok etkisi buna göre güncellenir).' : cfg.subtitle}</p>
        </div>
      </div>

      {/* Başlık */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>Belge Bilgileri</h3>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>Tarih *</label>
            <input style={s.input} type="date" value={docDate} disabled={isView} onChange={(e) => setDocDate(e.target.value)} />
          </div>
          {/* #60 Tarihin hemen yanında Distribütör; depo listesi buna bağlı çalışır.
              #738.3 Distribütör tipi kullanıcıda kilitli (kendi distribütörü otomatik). */}
          <div style={s.formGroup}>
            <label style={s.label}>Distribütör *</label>
            <RelationPicker
              config={RELATION_CONFIGS.vendor}
              rows={vendors}
              value={vendorId}
              selectedLabel={vendorId ? vendorLabel(vendorId) : ''}
              onChange={(val) => setVendorId(val || '')}
              disabled={isView || (isDistributor && !!lockedVendorId)}
              placeholder="Distribütör seçiniz"
            />
          </div>
          {!cfg.transfer && (
            <div style={s.formGroup}>
              <label style={s.label}>Depo *</label>
              <RelationPicker
                config={RELATION_CONFIGS.warehouse}
                rows={scopedWarehouses}
                value={warehouseId}
                selectedLabel={warehouseId ? warehouseLabel(warehouseId) : ''}
                onChange={(val) => setWarehouseId(val || '')}
                disabled={isView || !vendorId}
                placeholder={vendorId ? 'Depo seçiniz' : 'Önce distribütör seçin'}
              />
            </div>
          )}
          {cfg.transfer && (
            <>
              <div style={s.formGroup}>
                <label style={s.label}>Çıkış Deposu *</label>
                <RelationPicker
                  config={RELATION_CONFIGS.warehouse}
                  rows={scopedWarehouses}
                  value={sourceWarehouseId}
                  selectedLabel={sourceWarehouseId ? warehouseLabel(sourceWarehouseId) : ''}
                  onChange={(val) => setSourceWarehouseId(val || '')}
                  disabled={isView || !vendorId}
                  placeholder={vendorId ? 'Çıkış deposu' : 'Önce distribütör seçin'}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Giriş Deposu *</label>
                <RelationPicker
                  config={RELATION_CONFIGS.warehouse}
                  rows={scopedWarehouses}
                  value={targetWarehouseId}
                  selectedLabel={targetWarehouseId ? warehouseLabel(targetWarehouseId) : ''}
                  onChange={(val) => setTargetWarehouseId(val || '')}
                  disabled={isView || !vendorId}
                  placeholder={vendorId ? 'Giriş deposu' : 'Önce distribütör seçin'}
                />
              </div>
            </>
          )}
        </div>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>Belge No</label>
            <input style={s.input} value={documentNo} maxLength={100} disabled={isView} onChange={(e) => setDocumentNo(e.target.value)} placeholder={isView ? '' : 'Opsiyonel'} />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Açıklama</label>
            <input style={s.input} value={description} maxLength={500} disabled={isView} onChange={(e) => setDescription(e.target.value)} placeholder={isView ? '' : 'Opsiyonel'} />
          </div>
        </div>
        {cfg.transfer && sourceWarehouseId && targetWarehouseId && sourceWarehouseId === targetWarehouseId && (
          <p style={{ margin: '8px 0 0', color: '#E53E3E', fontSize: 13, fontWeight: 600 }}>
            Çıkış ve giriş deposu aynı olamaz.
          </p>
        )}
      </div>

      {/* Kalemler */}
      <div style={cardStyle}>
        <h3 style={sectionTitle}>Ürün Kalemleri</h3>

        {/* #765.1 "Ürün Kodu (ezbere) — Enter ile ekle" input'u kaldırıldı; ekleme yalnız rehberden. */}
        <div style={{ ...s.formRow, alignItems: 'flex-end' }}>
          <div style={{ ...s.formGroup, flex: 1 }}>
            <label style={s.label}>Ürün ekle (rehberden ara)</label>
            <RelationPicker
              config={RELATION_CONFIGS.product}
              value={pickerValue}
              selectedLabel=""
              onChange={(val) => addById(val)}
              disabled={isView || !headerReady}
              placeholder={headerReady ? 'Ürün kodu veya adı ile ara…' : headerHint}
            />
            {/* #766 Başlık (distribütör/depo) dolmadan kalem eklenemez. */}
            {!isView && !headerReady && (
              <span style={{ display: 'block', marginTop: 6, color: 'var(--text-secondary)', fontSize: 12 }}>{headerHint}.</span>
            )}
          </div>
        </div>

        <div style={{ marginTop: 14, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px', fontSize: 13, width: 40 }}>#</th>
                <th style={{ padding: '8px 6px', fontSize: 13 }}>Ürün</th>
                <th style={{ padding: '8px 6px', fontSize: 13, width: 160 }}>Birim</th>
                <th style={{ padding: '8px 6px', fontSize: 13, width: 140, textAlign: 'right' }}>Miktar</th>
                {/* #797.3 "Depo Miktarı" değil "Stok": kolon deponun adını değil, o depodaki stok
                    rakamını gösteriyor.
                    #748 (2. tur) Kolon Ürün'den sonra değil, MİKTAR–BİRİM'den HEMEN SONRA — ticari
                    belge ekranlarındaki (fatura/irsaliye/sipariş) yeni Stok kolonuyla aynı sırada. */}
                <th style={{ padding: '8px 6px', fontSize: 13, width: 130, textAlign: 'right' }} title="Belgede seçili depodaki kullanılabilir stok (satırın birimine çevrilmiş)">Stok</th>
                {/* #765.2 Birim fiyat (KDV hariç) — bilgi amaçlı. Backend StockDocumentLine'da karşılığı
                    YOK; yalnız UI'da tutulur, payload'a gönderilmez. */}
                <th style={{ padding: '8px 6px', fontSize: 13, width: 140, textAlign: 'right' }}>Birim Fiyat</th>
                <th style={{ padding: '8px 6px', fontSize: 13, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '18px 6px', color: 'var(--text-secondary)', fontSize: 13 }}>{isView ? 'Bu belgede kalem yok.' : 'Henüz ürün eklenmedi. Yukarıdan rehberden arayarak ekleyin.'}</td></tr>
              ) : (
                lines.map((l, i) => (
                  <tr key={l.key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '8px 6px', fontSize: 13, color: 'var(--text-secondary)' }}>{i + 1}</td>
                    <td style={{ padding: '8px 6px', fontSize: 13, fontWeight: 600 }}>{l.label}</td>
                    <td style={{ padding: '8px 6px' }}>
                      <select style={{ ...s.select, minWidth: 130 }} value={l.unitName} disabled={isView} onChange={(e) => onUnitChange(l.key, e.target.value)}>
                        {l.units.map((u) => <option key={u.name} value={u.name}>{u.name}{u.multiplier !== 1 ? ` (x${u.multiplier})` : ''}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <input
                        style={{ ...s.input, width: 120, textAlign: 'right' }}
                        type="number" step="any" min="0"
                        value={l.quantity}
                        disabled={isView}
                        onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                      />
                    </td>
                    {/* #722/#738 Seçili depodaki bakiye. Depo/distribütör yoksa "—", çekilirken "…".
                        #797.2 Satırın birimine çevrilir. #799 Kalem girerken rakam DEĞİŞMEZ: satır
                        eklendiğinde miktar varsayılan 1 geldiği için stok 10 iken 11/9 görünüyordu.
                        Belge kaydedilince `reloadStock()` defterdeki yeni bakiyeyi okur. */}
                    <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {(() => {
                        if (!balanceReady) return '—';
                        const v = balanceOf(l.productId);
                        if (v === undefined || v === 'loading') return '…';
                        if (v === null) return '—';
                        const mul = l.units.find((u) => u.name === l.unitName)?.multiplier || 1;
                        return formatBalance(Number(v) / (mul > 0 ? mul : 1));
                      })()}
                    </td>
                    {/* #765.2 Birim fiyat girişi (bilgi amaçlı). */}
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <input
                        style={{ ...s.input, width: 120, textAlign: 'right' }}
                        type="number" step="any" min="0"
                        value={l.unitPrice ?? ''}
                        disabled={isView}
                        onChange={(e) => updateLine(l.key, { unitPrice: e.target.value })}
                        placeholder="0,00"
                      />
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                      {!isView && <button type="button" title="Sil"
                        style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                        onClick={() => removeLine(l.key)}>×</button>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {lines.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 13, color: 'var(--text-secondary)' }}>
            <span>Kalem: <strong style={{ color: 'var(--text-primary)' }}>{lines.length}</strong></span>
            <span>Toplam (taban birim): <strong style={{ color: 'var(--text-primary)' }}>{totalBase.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}</strong></span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        {canSubmit && (
          <button style={{ ...s.btnPrimary, opacity: (saving || editLoading) ? 0.7 : 1 }} onClick={handleSave} disabled={saving || editLoading}>
            {saving ? 'Kaydediliyor…' : editLoading ? 'Yükleniyor…' : (isEdit ? 'Değişiklikleri Kaydet' : cfg.saveLabel)}
          </button>
        )}
        {!isView && <button style={s.btnSecondary} onClick={resetForm} disabled={saving || editLoading}>Temizle</button>}
      </div>

      <Toast toast={toast} />
    </div>
  );
}

export default StockDocumentPage;
